import React from "react";

import ChildrensBookManuscriptIllustrationContext
  from "./ChildrensBookManuscriptIllustrationContext";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


const SHARED_PAGE_PROFILES =
  Object.freeze({
    small_illustration:
      Object.freeze({
        visualHeightFraction:
          0.25,
        interRegionGapPoints:
          9,
        presentation:
          "shared_small",
        labelEn:
          "Small illustration",
        labelFr:
          "Petite illustration",
      }),

    half_page:
      Object.freeze({
        visualHeightFraction:
          0.5,
        interRegionGapPoints:
          12,
        presentation:
          "shared_half",
        labelEn:
          "Half-page illustration",
        labelFr:
          "Illustration sur une demi-page",
      }),
  });


export function manuscriptSharedIllustrationPageLayoutApplies(
  unit = {}
) {
  const unitType =
    clean(
      unit?.unitType
    ).toLowerCase();

  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  return (
    unitType ===
      "single_page" &&
    Boolean(
      SHARED_PAGE_PROFILES[
        illustrationPlan
      ]
    )
  );
}


export function manuscriptSharedIllustrationPageProfile(
  unit = {}
) {
  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  return (
    SHARED_PAGE_PROFILES[
      illustrationPlan
    ] ||
    null
  );
}


export default function ChildrensBookManuscriptSharedIllustrationPageLayout({
  lang = "en",
  unit,
  pageNumber,
  childrensBookAssets = [],
  onLoadChildrensBookAssetFile,
  editorOwnsIllustration = false,
  children,
}) {
  if (
    !manuscriptSharedIllustrationPageLayoutApplies(
      unit
    )
  ) {
    return children;
  }

  const isFR =
    clean(
      lang
    ).toLowerCase() ===
      "fr";

  const profile =
    manuscriptSharedIllustrationPageProfile(
      unit
    );

  const numericPage =
    Number(
      pageNumber
    );

  const pageLabel =
    Number.isInteger(
      numericPage
    ) &&
    numericPage > 0
      ? `Page ${numericPage}`
      : (
          isFR
            ? "Page"
            : "Page"
        );

  const illustrationLabel =
    isFR
      ? profile.labelFr
      : profile.labelEn;

  return (
    <section
      data-testid="manuscript-shared-illustration-page-layout"
      data-illustration-plan={
        clean(
          unit?.illustrationPlan
        )
      }
      data-visual-height-fraction={
        String(
          profile
            .visualHeightFraction
        )
      }
      data-production-gap-points={
        String(
          profile
            .interRegionGapPoints
        )
      }
      aria-label={
        isFR
          ? `${pageLabel} — illustration et texte`
          : `${pageLabel} — illustration and manuscript`
      }
      style={{
        display:
          "grid",
        gap:
          editorOwnsIllustration
            ? 12
            : 14,
        padding:
          editorOwnsIllustration
            ? 0
            : 18,
        border:
          editorOwnsIllustration
            ? "none"
            : "1px solid #94a3b8",
        borderRadius:
          editorOwnsIllustration
            ? 0
            : 16,
        background:
          editorOwnsIllustration
            ? "transparent"
            : "#ffffff",
      }}
    >
      <div
        style={{
          display:
            "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          gap:
            12,
          flexWrap:
            "wrap",
        }}
      >
        <strong
          style={{
            fontSize:
              18,
          }}
        >
          {pageLabel}
        </strong>

        <span
          style={{
            fontSize:
              13,
            fontWeight:
              800,
            color:
              "#475569",
          }}
        >
          {illustrationLabel}
        </span>
      </div>

      {!editorOwnsIllustration ? (
        <div
          data-testid="manuscript-shared-illustration-region"
        >
          <ChildrensBookManuscriptIllustrationContext
            lang={lang}
            unit={unit}
            childrensBookAssets={
              childrensBookAssets
            }
            onLoadChildrensBookAssetFile={
              onLoadChildrensBookAssetFile
            }
            presentation={
              profile.presentation
            }
          />
        </div>
      ) : null}

      <div
        data-testid="manuscript-shared-text-region"
        style={{
          display:
            "grid",
          gap:
            12,
          paddingTop:
            4,
        }}
      >
        {children}
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin:
            0,
          fontSize:
            12,
          lineHeight:
            1.45,
        }}
      >
        {isFR
          ? "Cette vue reflète la part prévue entre l’image et le texte. La géométrie finale dépend du format de production."
          : "This writing view reflects the planned image and text share. Final geometry depends on the production format."}
      </p>
    </section>
  );
}
